/// <amd-module name="VSS/Platform/History" />
import { IVssObservableService, IVssPageContext, ServiceOptions, Services, VssObservableService } from "./Context";
import { ContributionServiceEvent, IDataProviderQueryStartingEvent, IVssContributionService } from "./Contribution";
import { IMatchedContributedRoute, IVssNavigationService } from "./Navigation";
import { IPageData, IVssPageService } from "./Page";
import { combineUrlPaths, getBestRouteMatch, Uri } from "./Util/Url";

/**
 * Names of events triggered by the IVssHistoryService
 */
export namespace HistoryServiceEventNames {
    /**
     * Event triggered when a browser history entry has been popped (back or forward navigate).
     * Event object type: INavigationStateChangedEvent
     */
    export const statePopped = "statePopped";
}

/**
 * Service to manage browser history and navigation state
 *
 * Events:
 *    statePopped: INavigationStateChangedEvent
 *                 Triggered when a history entry has been popped by the browser (fwd/back navigation)
 */
export interface IVssHistoryService extends IVssObservableService<INavigationStateChangedEvent> {
    /**
     * Updates (replaces) the current history entry, optionally updating the address bar with a new url
     *
     * @param state Optional set of current URL state parameters (route values and query parameters). If not specified, computed from the given url.
     * @param url Optional url to update the address bar with. If not specified, computed from the given state (and current page's route template).
     * @param routeId Optional route id to set for this entry. If not specified, the current page's route id is used.
     * @param data Optional data values to merge into the current state entry's data property bag (state that is not related to the URL)
     */
    replaceState(state: { [key: string]: string } | undefined, url?: string, routeId?: string, data?: { [key: string]: any }): void;

    /**
     * Adds a new history entry, updating the address bar with a new url
     *
     * @param state Optional set of current URL state parameters (route values and query parameters). If not specified, computed from the given url.
     * @param url Optional url to update the address bar with. If not specified, computed from the given state (and current page's route template).
     * @param routeId Optional route id to set for this entry. If not specified, the current page's route id is used.
     * @param data Optional data values to set as the new state entry's data property bag (state that is not related to the URL)
     */
    pushState(state: { [key: string]: string } | undefined, url?: string, routeId?: string, data?: { [key: string]: any }): void;

    /**
     * Get the current state entry's data property bag (state that is not related to the URL parameters/route values).
     * This is the value of the property bag that was passed as the 'data' argument of the pushState/replaceState methods.
     */
    getData(): { [key: string]: any } | undefined;

    /**
     * Get the current dictionary of all URL state parameters. This includes route values as well as query parameters.
     */
    getState(): { [key: string]: string };

    /**
     * Generate a URL given the current navigation state values
     *
     * @param state State values (query parameters and route values)
     * @param mergeOptions Options around whether the provided state just provides overrides for the current navigation
     *        state or a full set of state values. The default is to merge with the current navigation state.
     */
    generateUrl(state: { [key: string]: string }, mergeOptions?: StateMergeOptions): string;

    /**
     * Get the current value for all the routeValues used for the current page.
     */
    getCurrentRouteValues(): { [key: string]: string };
}

/**
 * The state pushed in a single history entry
 */
export interface INavigationHistoryEntry {
    /**
     * Dictionary of all URL state parameters used in this history entry. This includes
     * route values as well as query parameters.
     */
    state: { [key: string]: string };

    /**
     * The id of the route that this entry is using
     */
    routeId: string;

    /**
     * Dictionary of history state data to keep which is not related to the URL parameters/route values.
     */
    data: { [key: string]: any };

    /**
     * The url for this entry
     */
    url: string;
}

/**
 * Event object sent in events where the navigation state changed.
 */
export interface INavigationStateChangedEvent {
    /**
     * The previous history entry that we navigated away from
     */
    oldState?: INavigationHistoryEntry;

    /**
     * The new/current history entry
     */
    newState?: INavigationHistoryEntry;
}

/**
 * Delegate called when navigation state has changed
 */
export interface INavigationStateChangedHandler {
    (stateChangedEvent: INavigationStateChangedEvent): void;
}

/**
 * Set of options used when supplying a state dictionary for generating a URL, or when
 * modifying existing state.
 */
export const enum StateMergeOptions {
    /**
     * Don't merge any current values into the state object. The given state object
     * represents all state values to use.
     */
    none,

    /**
     * Merge with the current route values (those defined in the route template).
     * Current query parameters are NOT merged into the given state object. Values for the current route
     * values will be overridden by entries in the given state object.
     */
    routeValues,

    /**
     * Merge with all current navigation state values. The given state just overrides entries in
     * the current navigation state.
     */
    currentState
}

/**
 * Use a unique key in the state we pass to the history service so that we
 * only operate on our "own" entries.
 */
interface IHistoryState {
    vssNavigationState: INavigationHistoryEntry;
}

/**
 * Service to manage browser history and navigation state
 */
class VssHistoryService extends VssObservableService<INavigationStateChangedEvent> implements IVssHistoryService {
    private _currentState: INavigationHistoryEntry | undefined;
    private _pageData: IPageData;
    private _currentRoute: IMatchedContributedRoute;
    private _unsubscribeOnRestart: () => void;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        this._initialize();

        window.addEventListener("popstate", this._onPopState);

        // Call replaceState with undefined to populate this._currentState based on the current URL.
        if (this._currentRoute) {
            this.replaceState(undefined, undefined);
        }
    }

    public _serviceRestart(pageContext: IVssPageContext) {
        super._serviceRestart(pageContext);
        this._initialize();

        // After FPS (service restart), re-compute our state using the current URL and route template.
        this.replaceState(undefined, window.location.href);
    }

    public replaceState(state: { [key: string]: string } | undefined, url?: string, routeId?: string, data?: { [key: string]: any }) {
        this._setState(true, state, url, routeId, data);
    }

    public pushState(state: { [key: string]: string } | undefined, url?: string, routeId?: string, data?: { [key: string]: any }) {
        this._setState(false, state, url, routeId, data);
    }

    public getData(): { [key: string]: any } | undefined {
        return this._currentState && this._currentState.data;
    }

    public getState(): { [key: string]: string } {
        let state: { [key: string]: string };

        // Get the state from the url
        if (this._currentState && this._currentState.state) {
            state = { ...this._currentState.state };
        } else {
            state = this._getStateFromUrl(window.location.href);
        }

        return state;
    }

    /**
     * Generate a URL given the current navigation state values
     *
     * @param state State values (query parameters and route values)
     * @param mergeOptions Options around whether the provided state just provides overrides for the current navigation
     *        state or a full set of state values. The default is to merge with the current navigation state.
     */
    public generateUrl(state: { [key: string]: string }, mergeOptions: StateMergeOptions = StateMergeOptions.currentState): string {
        const routeValues = this._currentRoute.routeValues;

        let fullState: { [key: string]: string };
        switch (mergeOptions) {
            case StateMergeOptions.currentState:
                fullState = { ...this.getState(), ...state };
                break;

            case StateMergeOptions.routeValues:
                fullState = { ...this.getCurrentRouteValues(), ...state };
                break;

            default:
                fullState = state;
        }

        const matchedRoute = getBestRouteMatch(this._currentRoute.parsedRoutes, fullState);

        let relativeUrl = "";
        if (matchedRoute) {
            relativeUrl = matchedRoute.url;
        }

        const uri = new Uri(combineUrlPaths(this._pageData.hostPath, relativeUrl));

        if (matchedRoute) {
            // Add state that was not used as a route replacement as a query parameter.
            for (const routeValueKey in fullState) {
                // Skip route values that were matched in the route template
                if (!matchedRoute.matchedParameters[routeValueKey]) {
                    const remainingStateValue = fullState[routeValueKey];
                    if (remainingStateValue !== null && remainingStateValue !== undefined) {
                        // Don't add default route values as query parameters.
                        if (routeValues[routeValueKey] !== remainingStateValue) {
                            uri.addQueryParam(routeValueKey, remainingStateValue);
                        }
                    }
                }
            }
        }

        return uri.absoluteUri;
    }

    /**
     * Get the current value for all the routeValues used for the current page.
     */
    public getCurrentRouteValues(): { [key: string]: string } {
        const pageRouteValues = this._currentRoute.routeValues;
        const routeValues: { [key: string]: string } = {};

        const state = this.getState();
        for (const routeValueKey in pageRouteValues) {
            routeValues[routeValueKey] = state[routeValueKey];
        }

        return routeValues;
    }

    private _initialize(): void {
        const pageService = this.pageContext.getService<IVssPageService>("IVssPageService");
        this._pageData = pageService.getData()!;

        const navigationService = this.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        this._currentRoute = navigationService.getCurrentRoute();

        if (this._unsubscribeOnRestart) {
            this._unsubscribeOnRestart();
        }

        // Add source page/route information to each asynchronous data provider query's properties
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService.subscribe(this.onbeginDataProviderQuery, ContributionServiceEvent.DataProviderQueryStarting);
        this._unsubscribeOnRestart = () => {
            contributionService.unsubscribe(this.onbeginDataProviderQuery, ContributionServiceEvent.DataProviderQueryStarting);
        };
    }

    private onbeginDataProviderQuery = (beginDataProviderQuery: IDataProviderQueryStartingEvent): void => {
        beginDataProviderQuery.properties["sourcePage"] = {
            url: window.location.href,
            routeId: this._currentRoute.routeId,
            routeValues: this.getCurrentRouteValues()
        };
    };

    private _setState(replace: boolean, state?: { [key: string]: string }, url?: string, routeId?: string, data?: { [key: string]: any }) {
        if (url && !state) {
            // Provided only the url. Get the state from the url parameters
            state = this._getStateFromUrl(url);
        } else if (state && !url) {
            // Provided only the state. Set the url based on the state.
            url = this.generateUrl(state, StateMergeOptions.none);
        } else if (!url && !state) {
            // Provided neither a url or state. Don't modify them. Only change routeId/navigationElementId.
            url = window.location.href;

            state = this._currentState! && this._currentState!.state;
            if (!state) {
                state = this._getStateFromUrl(url);
            }
        }

        if (routeId === null || routeId === undefined) {
            routeId = this._currentRoute.routeId;
        }

        const prevHistoryState = window.history.state as IHistoryState;

        let newData: { [key: string]: any } = {};
        if (replace && prevHistoryState && prevHistoryState.vssNavigationState) {
            newData = prevHistoryState.vssNavigationState.data;
        }

        if (data) {
            // Merge-in the new data values
            newData = { ...newData, ...data };
        }

        const navigationState: INavigationHistoryEntry = {
            state: state!,
            routeId: routeId,
            data: newData,
            url: url!
        };

        const historyState: IHistoryState = replace
            ? { ...prevHistoryState, vssNavigationState: navigationState }
            : { vssNavigationState: navigationState };
        this._currentState = navigationState;

        // Don't ever push a new history entry if it is for the same url as the current entry.
        if (replace || window.location.href === url) {
            window.history.replaceState(historyState, undefined!, url);
        } else {
            window.history.pushState(historyState, undefined!, url);
        }
    }

    private _getStateFromUrl(url: string): { [key: string]: string } {
        const state = { ...this._currentRoute.routeValues };

        if (url) {
            const uri = new Uri(url);
            for (const queryParameter of uri.queryParameters) {
                state[queryParameter.name] = queryParameter.value || "";
            }
        }

        return state;
    }

    private _onPopState = (e: PopStateEvent) => {
        const historyState = e.state as IHistoryState;

        let newState: INavigationHistoryEntry | undefined;
        if (historyState && historyState.vssNavigationState) {
            newState = historyState.vssNavigationState;
        }

        const oldState = this._currentState;
        this._currentState = newState;

        const popStateEvent: INavigationStateChangedEvent = {
            oldState,
            newState
        };

        this._notify(popStateEvent, HistoryServiceEventNames.statePopped);
    };
}

Services.add("IVssHistoryService", { serviceFactory: VssHistoryService, options: ServiceOptions.Persistant | ServiceOptions.Startup });
